/**
 * CancelBookingPage
 *
 * Intermediate page that prevents email link prefetchers from
 * auto-cancelling appointments. Patient must click a button
 * which POSTs to the backend to actually cancel.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { XCircle, Phone, Mail, Loader } from 'lucide-react';

import API_BASE_URL from '../config/api';
const API_URL = API_BASE_URL;

function CancelBookingPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/booking/cancel/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await res.json();

      if (result.success) {
        const params = new URLSearchParams({
          name: result.patientName || '',
          rebooking: result.rebookingSent ? 'true' : 'false',
        });
        navigate(`/booking-cancelled?${params}`, { replace: true });
      } else if (result.alreadyCancelled) {
        navigate('/booking-cancelled?already=true', { replace: true });
      } else if (result.pastAppointment) {
        navigate('/booking-cancelled?reason=past', { replace: true });
      } else {
        navigate(`/booking-cancelled?reason=${encodeURIComponent(result.error || 'unknown')}`, { replace: true });
      }
    } catch (err) {
      setError('Unable to connect to the server. Please try again or contact reception.');
      setCancelling(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 40,
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#fee',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <XCircle size={48} color="#d9534f" />
        </div>

        <h1 style={{ color: '#333', marginBottom: 15 }}>
          Cancel Your Appointment?
        </h1>

        <p style={{ color: '#666', marginBottom: 10, fontSize: 16 }}>
          Are you sure you want to cancel your appointment?
        </p>

        <p style={{ color: '#666', marginBottom: 25, fontSize: 14 }}>
          We'll send you new booking options so you can reschedule at a time that suits you better.
        </p>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #d9534f',
            padding: 15,
            borderRadius: 8,
            marginBottom: 20,
            color: '#d9534f',
            fontSize: 14,
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleCancel}
          disabled={cancelling}
          style={{
            backgroundColor: cancelling ? '#999' : '#d9534f',
            color: 'white',
            border: 'none',
            padding: '15px 40px',
            borderRadius: 8,
            fontSize: 18,
            fontWeight: 'bold',
            cursor: cancelling ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 25,
          }}
        >
          {cancelling ? (
            <>
              <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
              Cancelling...
            </>
          ) : (
            'Yes, Cancel Appointment'
          )}
        </button>

        <div style={{
          backgroundColor: '#f5f5f5',
          padding: 20,
          borderRadius: 8,
          marginTop: 10,
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: 10 }}>Need help?</p>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <Phone size={18} />
            <a href="tel:079576064" style={{ color: '#e8941c', fontWeight: 'bold' }}>07 957 6064</a>
          </p>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Mail size={18} />
            <a href="mailto:cambridge@angsm.co.nz" style={{ color: '#e8941c' }}>cambridge@angsm.co.nz</a>
          </p>
        </div>

        <p style={{ marginTop: 25, fontSize: 14, color: '#999' }}>
          Anglesea Sports Medicine | Cambridge, New Zealand
        </p>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default CancelBookingPage;
